import { useCallback, useEffect, useState } from 'react';
import Container from '@material-ui/core/Container';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import Head from '../../../components/Head';
import authService from '../../../services/useCases/auth';
import useQuery from '../../../hooks/useQuery';
import AuthHeader from '../../../components/Auth/Header';
import Card from '../../../components/Auth/Card';
import ErrorMessage from '../../../components/ErrorMessage';
import { LockIcon } from '../../../assets/icons';
import useAuthStyles from '../../../styles/pages/auth';
import PwdSended from './PwdChanged';

interface Values {
  password: string;
  confirmPassword: string;
}

const validationSchema = Yup.object().shape({
  password: Yup.string().required('Senha é obrigatória'),
  confirmPassword: Yup.string().required('Senha é obrigatória'),
});

export default function NewPassword() {
  // const classes = useStyles();
  const authClasses = useAuthStyles();
  const query = useQuery();

  const [token, setToken] = useState<string>();
  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);

  useEffect(() => {
    const token = query.get('t');
    if (token) setToken(token);
  }, [query]);

  const handleFormik = async (
    values: Values,
    formikHelpers: FormikHelpers<Values>
  ) => {
    try {
      await authService.resetPassword(
        token,
        values.password,
        values.confirmPassword
      );
      formikHelpers.resetForm({ values: {} as Values });
      setHasSuccess(true);
      setHasError(false);
    } catch (error) {
      console.log('erro', error);
      setHasError(true);
      setHasSuccess(false);
    }
    formikHelpers.setSubmitting(false);
  };

  const handleClose = useCallback(() => {
    setHasError(false);
    setHasSuccess(false);
  }, []);

  if (hasSuccess) return <PwdSended />;

  return (
    <Container maxWidth="sm" style={{ zIndex: 2 }}>
      <Head>Nova senha</Head>
      <Formik
        initialValues={{} as Values}
        validationSchema={validationSchema}
        onSubmit={handleFormik}
      >
        {({ isSubmitting, values, handleChange, touched, errors }) => (
          <Form className={authClasses.container}>
            <Grid container className={authClasses.cardWrapper}>
              <AuthHeader outside />
              <Card>
                <CardContent>
                  <Grid
                    item
                    xs={12}
                    className={authClasses.descriptionContainer}
                  >
                    <Typography variant="h1">Crie uma Nova Senha!</Typography>
                    <Typography>
                      Para voltar a acessar o app NOSSA CASA. Lembre-se de usar
                      pelo menos uma letra maiúscula e um número para criar uma
                      senha segura.
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                    className={authClasses.inputsContainer}
                  >
                    <Grid item xs={12}>
                      <TextField
                        id="password"
                        name="password"
                        label="Senha"
                        type="password"
                        value={values.password}
                        onChange={handleChange}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        size="small"
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <LockIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="confirmPassword"
                        name="confirmPassword"
                        label="Confirmar Senha"
                        type="password"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        error={
                          touched.confirmPassword &&
                          Boolean(errors.confirmPassword)
                        }
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                        size="small"
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <LockIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ErrorMessage
                        open={hasError || hasSuccess}
                        handleClose={handleClose}
                        title={hasError ? 'Erro' : 'Sucesso'}
                        severity={hasError ? 'error' : 'success'}
                        message={
                          hasError ? (
                            <>Erro ao alterar a senha. Favor tente novamente</>
                          ) : (
                            <>Senha alterada com sucesso</>
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        size="large"
                        disabled={isSubmitting}
                        className={authClasses.submitButton}
                      >
                        {isSubmitting ? 'Aguarde...' : 'Recuperar senha'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
